/**
 * QMP-FE-6 (F4) — WHO IS READING, AND WHO BELONGS TO WHICH COMPANY.
 *
 * `GET /admin/users?project_id=` opens at `viewer` for every member of the
 * project and serves, for each person, `project_company_id` — the
 * PARTICIPATION resolved by the same function the fences read
 * (`resolve_user_project_company`) — plus `project_role`, `access_revoked` and
 * `qmp_signatory` (D15.2), fail-closed by reading.
 *
 * WHAT THIS IS FOR, AND WHAT IT IS NOT FOR. It is ADDRESSING: whose row is
 * whose, and who can be named as the person a company's approval request goes
 * to. It is NOT authority. Step 7 has no `can_approve` on the wire, so the
 * screen never concludes from `qmp_signatory` that an act will be accepted —
 * the door decides, every time, and its refusal is what the page shows.
 *
 * A read that fails leaves everything empty: no row is claimed as "yours", so
 * nothing is offered that could not have been offered anyway.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAdminUsers } from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function useProjectMembership(projectId) {
  const { user } = useAuth() || {};
  const userId = user && user.id;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  useEffect(() => {
    if (!projectId) { setMembers([]); setLoading(false); return undefined; }
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    Promise.resolve(getAdminUsers(projectId))
      .then((res) => {
        if (mine !== seq.current) return;
        setMembers(Array.isArray(res && res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => {
        if (mine !== seq.current) return;
        setMembers([]);
        setLoading(false);
      });
    return () => { seq.current += 1; };
  }, [projectId]);

  const me = useMemo(
    () => members.find((m) => m && m.id === userId) || null,
    [members, userId],
  );

  /** The live people of one participation — who can be the address of that
   *  company's request. Revoked access is not a member any more, and the
   *  nominee door says so itself (422 `NOT_A_MEMBER`). */
  const membersOf = useCallback(
    (participationId) => (participationId
      ? members.filter((m) => m && m.project_company_id === participationId && !m.access_revoked)
      : []),
    [members],
  );

  const personById = useCallback(
    (id) => (id ? members.find((m) => m && m.id === id) || null : null),
    [members],
  );

  return {
    loading,
    members,
    me,
    myParticipationId: (me && me.project_company_id) || null,
    membersOf,
    personById,
  };
}
